import * as React from 'react';
import { withRouter } from 'common/components/core/router';
import compose from 'recompose/compose';
import { translate } from 'react-i18next';
import * as Immutable from 'immutable';
import classnames from 'classnames';
import isUndefined from 'lodash/isUndefined';

import DataUtils from 'common/utils/DataUtils';

import { CreateDistributionPlanConfig } from 'common/models/internal/CreateDistributionPlanConfig';
import { SalesCategoryGroup } from 'common/models/internal/SalesCategoryGroup';

import enhancedFieldFactory from 'common/components/core/form/EnhancedFieldFactory';
import { Dialog } from 'common/components/core/dialog';
import { Section } from 'common/entity/generic/SingleEntityEditView';
import Select from 'common/components/core/select/Select';
import { Button, ButtonTypes } from 'common/components/core/button';
import { HelpTooltip } from 'common/components/core/help';
import { EnhancedInput } from 'common/components/core/input';

import { SizeEnum } from 'common/enums/UIEnums';

import {
    outletSalesCategory,
    createDistributionPlan
} from 'common/middleware/api';

import theme from 'common/entity/pjpMaster/CreateDistBeatDialog.scss';

const EnhancedSelect = enhancedFieldFactory({
    FieldComponent: Select,
    attachPopover: true
});

class GroupSection extends React.PureComponent {
    onGroupChange = values => {
        const { groupIndex, onGroupChange } = this.props;
        const selectedSalesCategories = values.map(({ value }) => value);
        onGroupChange({
            groupIndex,
            selectedSalesCategories
        });
    };

    onCapacityChange = capacity => {
        const { groupIndex, onCapacityChange } = this.props;
        onCapacityChange({
            groupIndex,
            capacity:
                DataUtils.isEmpty(capacity) || Number(capacity) < 0
                    ? null
                    : Number(capacity)
        });
    };

    onDelete = () => {
        const { onDelete, groupIndex } = this.props;
        onDelete({ groupIndex });
    };

    render() {
        const {
            t,
            theme,
            salesCategories,
            capacity,
            groupIndex,
            categoryGroupSalesCategories,
            isSalesCategoryLoading,
            createDistPlanConfig
        } = this.props;

        return (
            <Section theme={theme}>
                <span className={theme.title}>
                    {t('pjpCreate.pjpConfig.deliveryGroupId', {
                        id: groupIndex + 1
                    })}
                    {groupIndex !== 0 ? (
                        <Button
                            buttonType={ButtonTypes.Icon}
                            circle
                            icon="delete-bin"
                            className={classnames(theme.btn, theme.deleteBtn)}
                            theme={theme}
                            onClick={this.onDelete}
                        />
                    ) : null}
                </span>
                <div className={theme.selectLabel}>
                    {t('masterRiderProperties.pjpData.salesCategories')}
                </div>
                <EnhancedSelect
                    isMulti
                    isSearchable
                    hasError={createDistPlanConfig.getValidationMessage(
                        'salesmanCategoryGroups'
                    )}
                    theme={theme}
                    className={theme.salesCategories}
                    blurInputOnSelect={false}
                    closeMenuOnSelect={false}
                    options={salesCategories}
                    isLoading={isSalesCategoryLoading}
                    onChange={this.onGroupChange}
                    value={categoryGroupSalesCategories}
                    error={createDistPlanConfig.getValidationMessage(
                        'salesmanCategoryGroups'
                    )}
                />
                <div className={theme.capacityLabel}>
                    {t('pjpCreate.pjpConfig.setCapacity')}
                </div>
                <EnhancedInput
                    theme={theme}
                    type="number"
                    rightIcon={t('entities:task', {
                        count: isUndefined(capacity) ? 0 : capacity
                    })}
                    className={theme.capacity}
                    onChange={this.onCapacityChange}
                    value={capacity}
                    autoWidth
                    columnInput
                    inputLabelSize={SizeEnum.Small}
                    placeholder={0}
                    showErrorIcon={createDistPlanConfig.getValidationMessage(
                        'salesmanCategoryGroupCapacities'
                    )}
                    error={createDistPlanConfig.getValidationMessage(
                        'salesmanCategoryGroupCapacities'
                    )}
                />
            </Section>
        );
    }
}

class CreateDistBeatDialog extends React.PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            isSubmitting: false,
            isSalesCategoryLoading: true,
            errorMessage: null,
            assignableSalesCategories: [],
            createDistPlanConfig: CreateDistributionPlanConfig.fromJS({
                distributionOffset: 2,
                salesCategoriesList: Immutable.List([]),
                salesmanCategoryGroups: Immutable.Map().set(
                    0,
                    SalesCategoryGroup.fromJS({
                        categories: []
                    })
                ),
                salesmanCategoryGroupCapacities: Immutable.Map().set(
                    0,
                    undefined
                )
            })
        };
    }

    componentDidMount() {
        const { createDistPlanConfig } = this.state;
        const { fetchOptions } = this.props;
        const { pjpId, clientId } = fetchOptions;

        outletSalesCategory
            .fetch({ clientId, pjpId })
            .then(categories => {
                const { salesCategories } = categories;
                const transformedSalesCategories = salesCategories.reduce(
                    (acc, val) => {
                        const obj = {
                            value: val.id,
                            label: val.name
                        };
                        return [...acc, obj];
                    },
                    []
                );

                const updatedConfig = createDistPlanConfig.setSalesCategoriesList(
                    Immutable.List(transformedSalesCategories)
                );

                this.setState({
                    createDistPlanConfig: updatedConfig,
                    assignableSalesCategories: transformedSalesCategories,
                    isSalesCategoryLoading: false
                });
            })
            .catch(error => {
                this.setState({
                    error
                });
            });
    }

    onCategoryGroupChange = ({ groupIndex, selectedSalesCategories }) => {
        const { createDistPlanConfig } = this.state;
        const { salesmanCategoryGroups } = createDistPlanConfig;

        const updatedConfig = createDistPlanConfig.setSalesmanCategoryGroups(
            salesmanCategoryGroups.set(
                groupIndex,
                SalesCategoryGroup.fromJS({
                    categories: selectedSalesCategories
                })
            )
        );

        const assignableSalesCategories = this.getSalesCategoryOptions(
            updatedConfig
        );

        this.setState({
            createDistPlanConfig: updatedConfig,
            assignableSalesCategories
        });
    };

    getSalesCategoryOptions(configuration) {
        const { salesmanCategoryGroups } = configuration;
        const salesCategories = configuration.getSalesCategoriesList();
        const assignedSalesCategories = Object.values(
            salesmanCategoryGroups.toJS()
        )
            .map(group => group.categories)
            .flat();

        const assignableSalesCategories = salesCategories?.filter(
            ({ value }) => !assignedSalesCategories.includes(value)
        );
        return assignableSalesCategories.toJS();
    }

    onDismiss = () => {
        const { onDismiss, reloadData } = this.props;
        reloadData();
        onDismiss();
    };

    validateForm = () => {
        const { createDistPlanConfig } = this.state;
        const updatedDistPlanConfig = createDistPlanConfig.setAllValidationErrors();
        this.setState({ createDistPlanConfig: updatedDistPlanConfig });

        return !updatedDistPlanConfig.hasValidationErrors();
    };

    clearErrorMessage = () => {
        // Clear message after 5 sec
        setTimeout(() => {
            this.setState({
                errorMessage: null
            });
        }, 5000);
    };

    onAccept = () => {
        const { createDistPlanConfig } = this.state;
        const {
            distributionOffset,
            salesmanCategoryGroups,
            salesmanCategoryGroupCapacities
        } = createDistPlanConfig;
        const { fetchOptions, onDismiss, reloadData, t } = this.props;
        const { pjpId, clientId } = fetchOptions;

        const params = { pjpId, clientId };
        const body = {
            configuration: {
                distributionOffset,
                salesmanCategoryGroups: salesmanCategoryGroups.toJS(),
                salesmanCategoryGroupCapacities
            }
        };

        if (this.validateForm()) {
            this.setState({ isSubmitting: true });
            createDistributionPlan
                .post(params, body)
                .then(() => {
                    this.setState({ isSubmitting: false }, () => {
                        onDismiss();
                        reloadData();
                    });
                })
                .catch(() => {
                    this.setState({ isSubmitting: false });
                });
        } else {
            this.setState(
                {
                    errorMessage: t(
                        'validations:genericFormValdiationFailMessage'
                    )
                },
                this.clearErrorMessage
            );
        }
    };

    onDeleteCategoryGroup = ({ groupIndex }) => {
        const { createDistPlanConfig, assignableSalesCategories } = this.state;
        const {
            salesmanCategoryGroups,
            salesmanCategoryGroupCapacities
        } = createDistPlanConfig;
        const salesCategories = createDistPlanConfig.getSalesCategoriesList();
        const { categories } = salesmanCategoryGroups.get(groupIndex);

        const removedCategories = salesCategories.filter(category => {
            return categories.includes(category.value);
        });

        this.setState({
            assignableSalesCategories: [
                ...assignableSalesCategories,
                ...removedCategories.toJS()
            ]
        });
        const updatedConfig = createDistPlanConfig
            .setSalesmanCategoryGroups(
                salesmanCategoryGroups.delete(groupIndex)
            )
            .setSalesmanCategoryGroupCapacities(
                salesmanCategoryGroupCapacities.delete(groupIndex)
            );

        this.setState({ createDistPlanConfig: updatedConfig });
    };

    onAddGroup = () => {
        const { createDistPlanConfig } = this.state;
        const {
            salesmanCategoryGroups,
            salesmanCategoryGroupCapacities
        } = createDistPlanConfig;
        const groupIndex = salesmanCategoryGroups.size;
        const updatedConfig = CreateDistributionPlanConfig.fromJS({
            salesmanCategoryGroups: salesmanCategoryGroups.set(
                groupIndex,
                SalesCategoryGroup.fromJS({
                    categories: []
                })
            ),
            salesmanCategoryGroupCapacities: salesmanCategoryGroupCapacities.set(
                groupIndex,
                undefined
            )
        });

        this.setState({
            createDistPlanConfig: updatedConfig.mergeDeep(createDistPlanConfig)
        });
    };

    onCategoryGroupCapacityChange = ({ groupIndex, capacity }) => {
        const { createDistPlanConfig } = this.state;
        const { salesmanCategoryGroupCapacities } = createDistPlanConfig;

        const updatedConfig = createDistPlanConfig.setSalesmanCategoryGroupCapacities(
            salesmanCategoryGroupCapacities.set(groupIndex, capacity)
        );

        this.setState({ createDistPlanConfig: updatedConfig });
    };

    renderCategoryGroups = () => {
        const {
            createDistPlanConfig,
            assignableSalesCategories,
            isSalesCategoryLoading
        } = this.state;

        const { t } = this.props;
        const { salesmanCategoryGroups } = createDistPlanConfig;

        return salesmanCategoryGroups
            .entrySeq()
            .map((categoryGroupSalesCategories, index) => {
                return (
                    <GroupSection
                        theme={theme}
                        t={t}
                        key={index}
                        groupIndex={index}
                        salesCategories={assignableSalesCategories}
                        categoryGroupSalesCategories={categoryGroupSalesCategories?.categories?.toJS()}
                        capacity={categoryGroupSalesCategories?.capacity}
                        onGroupChange={this.onCategoryGroupChange}
                        onCapacityChange={this.onCategoryGroupCapacityChange}
                        onDelete={this.onDeleteCategoryGroup}
                        isSalesCategoryLoading={isSalesCategoryLoading}
                        createDistPlanConfig={createDistPlanConfig}
                    />
                );
            });
    };

    render() {
        const { t } = this.props;
        const { isSubmitting, errorMessage } = this.state;

        return (
            <Dialog
                active
                theme={theme}
                className={theme.createDistContainer}
                acceptActionLabel={t('pjpTable.create')}
                title={t('pjpCreate.pjpConfig.createDistributionPlan')}
                onDismiss={this.onDismiss}
                onCancel={this.onDismiss}
                onAccept={this.onAccept}
                isLoading={isSubmitting}
                acceptErrorMessage={errorMessage}
            >
                <div className={theme.sectionGroup}>
                    <div className={theme.label}>
                        <span>
                            {t('pjpCreate.pjpConfig.deliveryGroup_plural')}:
                        </span>
                        <HelpTooltip
                            tooltipText={t(
                                'pjpCreate.pjpConfig.help.deliveryGroup'
                            )}
                        />
                    </div>
                    <div>
                        {this.renderCategoryGroups()}
                        <span
                            className={theme.addText}
                            onClick={this.onAddGroup}
                        >
                            {`+${t('pjpCreate.pjpConfig.addDeliveryGroup')}`}
                        </span>
                    </div>
                </div>
            </Dialog>
        );
    }
}

export default compose(
    withRouter,
    translate('entityManagement')
)(CreateDistBeatDialog);
